import { useState } from "react";
import { Link } from "react-router-dom";
import type { Topic } from "@/types/content";
import CommentCard from "@/components/base/CommentCard";
import { formatNumber } from "@/utils/format";

const filters = ["全部", "神评", "高能", "热评"] as const;

export default function TopicComments({ topic }: { topic: Topic }) {
  const [liked, setLiked] = useState<string[]>([]);
  const [active, setActive] = useState<(typeof filters)[number]>("全部");
  const [showAll, setShowAll] = useState(false);

  const toggleLike = (id: string) => {
    setLiked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const visible = topic.comments.filter((c) => active === "全部" || c.badge === active);
  const shown = showAll ? visible : visible.slice(0, 3);

  return (
    <section aria-label="评论区高能回复" className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-4 w-4 items-center justify-center text-primary-600">
            <i className="ri-chat-quote-line" aria-hidden="true" />
          </span>
          <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
            评论区高能回复
          </h2>
        </div>
        <span className="font-label text-xs text-foreground-500">
          共 {formatNumber(topic.commentCount)} 条讨论
        </span>
        <div className="ml-auto inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
          {filters.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => {
                setActive(item);
                setShowAll(false);
              }}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 font-label text-xs transition-colors ${
                active === item
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      {shown.length > 0 ? (
        <div className="mt-5 space-y-4">
          {shown.map((comment) => (
            <CommentCard
              key={comment.id}
              comment={comment}
              liked={liked.includes(comment.id)}
              onLike={toggleLike}
            />
          ))}
        </div>
      ) : (
        <p className="mt-5 rounded-md bg-background-200 px-4 py-6 text-center text-sm text-foreground-600">
          这个分类下暂时还没有被标记的回复，切换回全部看看。
        </p>
      )}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
        {visible.length > 3 ? (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 font-label text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={showAll ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true" />
            </span>
            {showAll ? "收起回复" : `展开剩余 ${visible.length - 3} 条回复`}
          </button>
        ) : (
          <span className="font-label text-xs text-foreground-500">
            以上为当前话题的高能回复精选
          </span>
        )}
        <Link
          to="/topics"
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap font-label text-xs text-secondary-700 transition-colors hover:text-primary-600"
        >
          去看其他话题的评论区
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </span>
        </Link>
      </div>
    </section>
  );
}